import { Box, Flex, useToast } from "@chakra-ui/react";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../config/axiosInstance";
import Filters from "./Filters";
import OrdersTable from "./OrdersTable";
import PaginationControls from "./PaginationControls";
import SearchBar from "./SearchBar";
import Stats from "./Stats";

export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [filters, setFilters] = useState({ status: [], paymentStatus: "", receptionistStatus: "" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [fromDate, setFromDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));

  const toast = useToast();

  // ✅ Fetch orders
  const fetchOrders = useCallback(async () => {
    const params = {
      page,
      limit: pageSize,
      search,
      ...filters,
      fromDate,
      toDate,
    };

    if (Array.isArray(params.status)) params.status = params.status.join(",");

    try {
      setOrderLoading(true);
      const res = await axiosInstance.get("/api/orders/receptionist", { params });
      setOrders(res.data.orders || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 0 });
    } catch (err) {
      toast({ title: "Error loading orders", status: "error" });
    } finally {
      setOrderLoading(false);
    }
  }, [page, pageSize, search, filters, fromDate, toDate, toast]);

  // ✅ Reset page to 1 whenever filters, search, or date changes
  useEffect(() => {
    setPage(1);
  }, [filters, search, fromDate, toDate]);

  // ✅ Fetch orders when page, pageSize, or filters/search/date changes
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSendOtp = async (order) => {
    toast({ title: `OTP sent to ${order.customer.phone}`, status: "success" });
  };

  const handleDelete = (order) => {
    toast({ title: `Order ${order.billNumber} deleted`, status: "info" });
  };

  const handleEdit = (order) => {
    toast({ title: `Editing order ${order.billNumber}` });
  };

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Stats
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
      />

      <Box p={4}>
        <Flex justify="space-between" wrap="wrap" mb={4} gap={3}>
          <SearchBar search={search} setSearch={setSearch} />
          <Filters
            filters={filters}
            setFilters={setFilters}
            fetchOrders={fetchOrders}
            resetPage={() => setPage(1)}
          />
        </Flex>

        <OrdersTable
          fetchOrders={fetchOrders}
          data={orders}
          onSendOtp={handleSendOtp}
          onDelete={handleDelete}
          onEdit={handleEdit}
          orderLoading={orderLoading}
        />

        <PaginationControls
          page={page}
          totalPages={pagination.totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={(size) => {
            setPageSize(size);
            setPage(1); // Reset page to 1 when changing page size
          }}
        />
      </Box>
    </Box>
  );
}
